import { Linking } from 'react-native';
import { logNonFatalError } from 'shared/utils/sentry/sentry';

const INVALID_URL_PLACEHOLDER = 'NA';

const isValidExternalUrl = (url?: string | null): url is string => {
  if (!url) {
    return false;
  }

  const trimmedUrl = url.trim();

  if (trimmedUrl.length === 0) {
    return false;
  }

  return trimmedUrl.toUpperCase() !== INVALID_URL_PLACEHOLDER;
};

/**
 * Safely opens an external URL provided by the backend.
 *
 * Backend-driven buttons can return placeholder values such as 'NA' or empty
 * strings when no URL is available. Passing these to Linking.openURL throws a
 * JSApplicationIllegalArgumentException on Android because no activity can
 * handle the intent. This guards against invalid and unsupported values so a
 * bad value never crashes the app.
 *
 * Returns true when the URL was opened, false otherwise.
 */
export const openExternalUrl = async (
  url?: string | null,
): Promise<boolean> => {
  if (!isValidExternalUrl(url)) {
    // No-op for invalid URLs. A user-facing toast/message could be added here.
    logNonFatalError(`openExternalUrl_invalid_url : ${String(url)}`);
    return false;
  }

  try {
    const isSupported = await Linking.canOpenURL(url);

    if (!isSupported) {
      // No-op for unsupported URLs. A user-facing toast/message could be added here.
      logNonFatalError(`openExternalUrl_unsupported_url : ${url}`);
      return false;
    }

    await Linking.openURL(url);
    return true;
  } catch (err) {
    // No-op on failure. A user-facing toast/message could be added here.
    logNonFatalError(`openExternalUrl_failed : ${url}, ${String(err)}`);
    return false;
  }
};
